import React, { useState, useEffect } from "react";
import { isPermissionDisabled } from "./ModulePermissions";

const ModulePermissionsGrid = ({
  name,
  label,
  selectedModules = [],
  helpText,
  disabled = false,
  setValue,
  getValues,
  edit = false,
  gisModuleIds = [],
}) => {
  // Internal state to manage permissions
  const [modulePermissions, setModulePermissions] = useState({});

  // IMPROVED: Better edit mode detection
  const detectEditMode = () => {
    const hasEditProp = edit === true;
    const hasFormId = getValues && getValues("id");
    const urlHasEdit =
      window.location.pathname.includes("/edit") ||
      window.location.pathname.includes("edit");
    const formHasExistingData =
      getValues && getValues("name") && getValues("name") !== "";

    return hasEditProp || hasFormId || urlHasEdit || formHasExistingData;
  };

  const isEditMode = detectEditMode();

  console.log("=== Edit Mode Detection ===");
  console.log("- edit prop:", edit);
  console.log("- form id:", getValues ? getValues("id") : "no getValues");
  console.log("- URL:", window.location.pathname);
  console.log("- form name:", getValues ? getValues("name") : "no getValues");
  console.log("- final isEditMode:", isEditMode);
  console.log("=== End Edit Mode Detection ===");

  // NEW: Helper to check if a specific permission is disabled for a module
  const isModulePermissionDisabled = (moduleName, permissionType) => {
    // First check configuration
    const isDisabledByConfig = isPermissionDisabled(moduleName, permissionType);

    console.log(
      `Checking permission for "${moduleName}" - ${permissionType}: ${isDisabledByConfig ? "DISABLED" : "ENABLED"}`
    );

    return isDisabledByConfig;
  };

  // Compact styles optimized for small space
  const styles = {
    container: {
      width: "100%",
      margin: 0,
      padding: "8px",
      gridColumn: "1 / -1",
      display: "flex",
      flexDirection: "column",
      maxHeight: "250px",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "black",
      marginBottom: "6px",
      lineHeight: "1.2",
    },
    helpText: {
      fontSize: "12px",
      fontWeight: "400",
      color: "#7f8c8d",
    },
    emptyContainer: {
      padding: "20px",
      textAlign: "center",
      background: "#f8f9fa",
      borderRadius: "6px",
      border: "1px dashed #dee2e6",
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    emptyMessage: {
      color: "#6c757d",
      fontSize: "14px",
      margin: 0,
      fontFamily: "Poppins, sans-serif",
      lineHeight: "1.3",
    },
    tableContainer: {
      flex: 1,
      maxHeight: "300px",
      overflow: "auto",
      border: "1px solid #e0e0e0",
      borderRadius: "4px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "16px",
      fontFamily: "Poppins, sans-serif",
    },
    thead: {
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
    headerRow: {
      background: "linear-gradient(135deg, #1a7a73 0%, #3a7a94 100%)",
      color: "white",
    },
    th: {
      padding: "6px 8px",
      textAlign: "center",
      fontWeight: "600",
      fontSize: "15px",
      borderRight: "1px solid rgba(255,255,255,0.2)",
      whiteSpace: "nowrap",
    },
    thFirst: {
      textAlign: "left",
      minWidth: "100px",
    },
    tbody: {},
    tr: {
      borderBottom: "1px solid #e0e0e0",
      transition: "background-color 0.2s ease",
    },
    trHover: {
      backgroundColor: "#f8f9fa",
    },
    // Style for restricted modules (NO/NA permissions)
    trRestricted: {
      backgroundColor: "#fff3cd",
      borderLeft: "3px solid #ffc107",
    },
    // Style for fully disabled modules (both edit and delete are NO/NA)
    trFullyRestricted: {
      backgroundColor: "#f8d7da",
      borderLeft: "3px solid #dc3545",
    },
    td: {
      padding: "4px 8px",
      borderRight: "1px solid #f0f0f0",
      verticalAlign: "middle",
    },
    tdFirst: {
      fontWeight: "500",
      color: "#2c3e50",
      fontSize: "14px",
      lineHeight: "1.2",
    },
    tdCenter: {
      textAlign: "center",
    },
    checkbox: {
      width: "14px",
      height: "14px",
      cursor: "pointer",
      accentColor: "#1a7a73",
      margin: 0,
    },
    checkboxDisabled: {
      cursor: "not-allowed",
      opacity: 0.5,
    },
    // Badge styles
    restrictionBadge: {
      marginLeft: "8px",
      fontSize: "11px",
      fontStyle: "italic",
      fontWeight: "400",
    },
    viewOnlyBadge: {
      color: "#856404",
    },
    editOnlyBadge: {
      color: "#004085",
    },
    noEditBadge: {
      color: "#721c24",
    },
  };

  const [hoveredRow, setHoveredRow] = useState(null);

  // FIXED: Initialize permissions when selectedModules change
  useEffect(() => {
    if (selectedModules.length > 0) {
      console.log("=== ModulePermissionsGrid useEffect ===");
      console.log("selectedModules:", selectedModules);
      console.log("isEditMode:", isEditMode);

      const existingModulePermissions = getValues
        ? getValues("modulePermissions")
        : null;
      console.log(
        "existingModulePermissions from form:",
        existingModulePermissions
      );

      let initialPermissions = {};
      let shouldInitialize = false;

      // In edit mode, use existing permissions if available
      if (
        isEditMode &&
        existingModulePermissions &&
        typeof existingModulePermissions === "object" &&
        Object.keys(existingModulePermissions).length > 0
      ) {
        console.log("Using existing permissions for edit mode");
        selectedModules.forEach((module) => {
          const moduleName = module.label || module.name;

          if (existingModulePermissions[module.id]) {
            // Check configuration for this module
            const editDisabled = isModulePermissionDisabled(moduleName, "edit");
            const deleteDisabled = isModulePermissionDisabled(
              moduleName,
              "delete"
            );

            initialPermissions[module.id] = {
              view:
                existingModulePermissions[module.id].view !== undefined
                  ? existingModulePermissions[module.id].view
                  : true,
              edit: editDisabled
                ? false
                : existingModulePermissions[module.id].edit || false,
              delete: deleteDisabled
                ? false
                : existingModulePermissions[module.id].delete || false,
            };

            console.log(`Module ${module.id} (${moduleName}):`, {
              editDisabled,
              deleteDisabled,
              permissions: initialPermissions[module.id],
            });
          } else {
            // Default for new module
            const editDisabled = isModulePermissionDisabled(moduleName, "edit");
            const deleteDisabled = isModulePermissionDisabled(
              moduleName,
              "delete"
            );

            initialPermissions[module.id] = {
              view: true,
              edit: editDisabled ? false : false,
              delete: deleteDisabled ? false : false,
            };
          }
        });
        shouldInitialize = true;
      } else {
        // Default permissions for create mode
        selectedModules.forEach((module) => {
          const moduleName = module.label || module.name;
          const editDisabled = isModulePermissionDisabled(moduleName, "edit");
          const deleteDisabled = isModulePermissionDisabled(
            moduleName,
            "delete"
          );

          initialPermissions[module.id] = {
            view: true,
            edit: editDisabled ? false : false,
            delete: deleteDisabled ? false : false,
          };
        });
        shouldInitialize = true;
        console.log("Using default permissions");
      }

      if (shouldInitialize) {
        console.log("Setting modulePermissions:", initialPermissions);
        setModulePermissions(initialPermissions);
        // setIsInitialized(true);

        if (
          setValue &&
          JSON.stringify(initialPermissions) !==
            JSON.stringify(existingModulePermissions)
        ) {
          setValue("modulePermissions", initialPermissions);
        }
      }

      console.log("=== End ModulePermissionsGrid useEffect ===");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModules, getValues, setValue, isEditMode]);

  // Watch for form updates in edit mode
  useEffect(() => {
    if (isEditMode && getValues) {
      const checkForUpdates = () => {
        const formPermissions = getValues("modulePermissions");
        if (
          formPermissions &&
          typeof formPermissions === "object" &&
          JSON.stringify(formPermissions) !== JSON.stringify(modulePermissions)
        ) {
          console.log(
            "Form modulePermissions updated externally:",
            formPermissions
          );

          // Enforce restrictions when updating
          const updatedPermissions = { ...formPermissions };
          Object.keys(updatedPermissions).forEach((moduleId) => {
            const module = selectedModules.find(
              (m) => m.id === parseInt(moduleId)
            );
            if (module) {
              const moduleName = module.label || module.name;
              const editDisabled = isModulePermissionDisabled(
                moduleName,
                "edit"
              );
              const deleteDisabled = isModulePermissionDisabled(
                moduleName,
                "delete"
              );

              if (editDisabled) {
                updatedPermissions[moduleId].edit = false;
              }
              if (deleteDisabled) {
                updatedPermissions[moduleId].delete = false;
              }
            }
          });

          setModulePermissions(updatedPermissions);
        }
      };

      const interval = setInterval(checkForUpdates, 500);
      return () => clearInterval(interval);
    }
  }, [getValues, modulePermissions, isEditMode, selectedModules]);

  // Handle permission change with restriction enforcement
  const handlePermissionChange = (
    moduleId,
    moduleName,
    permissionType,
    checked
  ) => {
    console.log(
      `Permission change: Module ${moduleId} (${moduleName}), ${permissionType} = ${checked}`
    );

    // Check if this permission is disabled by configuration
    if (isModulePermissionDisabled(moduleName, permissionType)) {
      console.log(
        `Blocked: Cannot modify ${permissionType} permission for module "${moduleName}" (disabled by configuration)`
      );
      return;
    }

    setModulePermissions((prev) => {
      const updated = {
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          [permissionType]: checked,
        },
      };

      console.log("Updated modulePermissions:", updated);

      if (setValue) {
        setValue("modulePermissions", updated);
      }

      return updated;
    });
  };

  // Helper to get badge text based on restrictions
  const getRestrictionBadge = (moduleName) => {
    const editDisabled = isModulePermissionDisabled(moduleName, "edit");
    const deleteDisabled = isModulePermissionDisabled(moduleName, "delete");

    if (editDisabled && deleteDisabled) {
      return { text: "", style: styles.viewOnlyBadge };
    } else if (editDisabled && !deleteDisabled) {
      return { text: "(No Edit)", style: styles.noEditBadge };
    } else if (!editDisabled && deleteDisabled) {
      return { text: "", style: styles.editOnlyBadge };
    }
    return null;
  };

  if (!selectedModules || selectedModules.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyContainer}>
          <p style={styles.emptyMessage}>
            Select modules to configure permissions
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <label style={styles.label}>
        {label}
        {helpText && <span style={styles.helpText}> - {helpText}</span>}
      </label>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr style={styles.headerRow}>
              <th style={{ ...styles.th, ...styles.thFirst }}>Module</th>
              <th style={styles.th}>View</th>
              <th style={styles.th}>Edit</th>
              <th style={styles.th}>Delete</th>
            </tr>
          </thead>
          <tbody style={styles.tbody}>
            {selectedModules.map((module, index) => {
              const modulePerms = modulePermissions[module.id] || {
                view: true,
                edit: false,
                delete: false,
              };
              const moduleName = module.label || module.name;

              // Check restrictions
              const editDisabled = isModulePermissionDisabled(
                moduleName,
                "edit"
              );
              const deleteDisabled = isModulePermissionDisabled(
                moduleName,
                "delete"
              );
              const bothDisabled = editDisabled && deleteDisabled;

              const badge = getRestrictionBadge(moduleName);

              return (
                <tr
                  key={module.id}
                  style={{
                    ...styles.tr,
                    ...(bothDisabled
                      ? styles.trFullyRestricted
                      : editDisabled || deleteDisabled
                        ? styles.trRestricted
                        : {}),
                    ...(hoveredRow === index && !bothDisabled
                      ? styles.trHover
                      : {}),
                  }}
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td style={{ ...styles.td, ...styles.tdFirst }}>
                    {moduleName}
                    {badge && (
                      <span
                        style={{ ...styles.restrictionBadge, ...badge.style }}
                      >
                        {badge.text}
                      </span>
                    )}
                  </td>

                  <td style={{ ...styles.td, ...styles.tdCenter }}>
                    <input
                      type="checkbox"
                      checked={modulePerms.view}
                      onChange={(e) =>
                        handlePermissionChange(
                          module.id,
                          moduleName,
                          "view",
                          e.target.checked
                        )
                      }
                      disabled={true}
                      style={{
                        ...styles.checkbox,
                        ...(disabled ? styles.checkboxDisabled : {}),
                      }}
                    />
                  </td>

                  <td style={{ ...styles.td, ...styles.tdCenter }}>
                    <input
                      type="checkbox"
                      checked={modulePerms.edit}
                      onChange={(e) =>
                        handlePermissionChange(
                          module.id,
                          moduleName,
                          "edit",
                          e.target.checked
                        )
                      }
                      disabled={disabled || editDisabled}
                      style={{
                        ...styles.checkbox,
                        ...(disabled || editDisabled
                          ? styles.checkboxDisabled
                          : {}),
                      }}
                      title={editDisabled ? "Edit actions are not applicable for this module" : "Toggle edit permission"}
                    />
                  </td>

                  <td style={{ ...styles.td, ...styles.tdCenter }}>
                    <input
                      type="checkbox"
                      checked={modulePerms.delete}
                      onChange={(e) =>
                        handlePermissionChange(
                          module.id,
                          moduleName,
                          "delete",
                          e.target.checked
                        )
                      }
                      disabled={disabled || deleteDisabled}
                      style={{
                        ...styles.checkbox,
                        ...(disabled || deleteDisabled
                          ? styles.checkboxDisabled
                          : {}),
                      }}
                      title={deleteDisabled ? "Delete actions are not applicable for this module" : "Toggle delete permission"}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ModulePermissionsGrid;
